import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe,
  faLanguage,
  faClock,
  faHandshake,
  faShieldHalved,
  faServer
} from '@fortawesome/free-solid-svg-icons';

const GlobalReachSection = styled.section`
  padding: 80px 0;
  background: var(--light-color);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at center, rgba(255, 77, 0, 0.05) 0%, transparent 70%);
    transform: rotate(-45deg);
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 60px;

  h2 {
    font-size: 36px;
    color: var(--dark-color);
    margin-bottom: 20px;
    font-weight: 700;
  }

  p {
    font-size: 18px;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    h2 {
      font-size: 28px;
    }
    p {
      font-size: 16px;
    }
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
`;

const FeatureCard = styled.div`
  background: var(--white);
  border-radius: var(--border-radius);
  padding: 30px;
  box-shadow: var(--box-shadow);
  transition: all 0.3s ease;
  text-align: center;
  border: 1px solid rgba(255, 77, 0, 0.1);

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(255, 77, 0, 0.1);
  }
`;

const IconWrapper = styled.div`
  width: 80px;
  height: 80px;
  background: var(--gradient);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 25px;
  font-size: 32px;
  color: var(--white);
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border: 2px solid var(--primary-color);
    border-radius: 50%;
    opacity: 0.2;
    transition: all 0.3s ease;
  }

  ${FeatureCard}:hover &::after {
    transform: scale(1.2);
    opacity: 0;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 20px;
  color: var(--dark-color);
  margin-bottom: 15px;
  font-weight: 600;
`;

const FeatureDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const features = [
  {
    icon: faGlobe,
    title: 'Global Reach',
    description: 'Connect with customers worldwide through our international platform and multi-language support.'
  },
  {
    icon: faLanguage,
    title: 'Multi-Language',
    description: 'Communicate in multiple languages with built-in translation and localization features.'
  },
  {
    icon: faClock,
    title: '24/7 Availability',
    description: 'Our platform runs round the clock, ensuring your business never sleeps.'
  },
  {
    icon: faHandshake,
    title: 'Local Compliance',
    description: 'Stay compliant with local regulations and data protection laws worldwide.'
  },
  {
    icon: faShieldHalved,
    title: 'Global Security',
    description: 'Enterprise-grade security measures protecting your data across all regions.'
  },
  {
    icon: faServer,
    title: 'Global Infrastructure',
    description: 'Powered by distributed servers ensuring fast performance worldwide.'
  }
];

const GlobalReach: React.FC = () => {
  return (
    <GlobalReachSection id="global">
      <Container>
        <SectionTitle data-aos="fade-up">
          <h2>🌍 Works Globally. No Restrictions.</h2>
          <p>Expand your business globally with our worldwide infrastructure and support.</p>
        </SectionTitle>
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard
              key={index}
              data-aos="fade-up"
              data-aos-delay={index * 100}
            >
              <IconWrapper>
                <FontAwesomeIcon icon={feature.icon} />
              </IconWrapper>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </GlobalReachSection>
  );
};

export default GlobalReach; 